'use client';

import { useEffect, useState } from 'react';
import { Bell, Check, Loader2 } from 'lucide-react';
import { useCartStore } from '@/stores/cart';
import { useAuthStore } from '@/stores/auth';
import { getTrackingData } from '@/lib/tracking';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';
import { StockNotifyForm } from '@/components/StockNotifyForm';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.autofrance.se/api';
const FLAGGA = 'af-forhandsvisa-bevaka';

/** Förhandsvisning tills ägaren sett formuläret: ?forhandsvisa=bevaka slår på, ?forhandsvisa=av slår av. */
export function useBevakaPa(): boolean {
  const [pa, setPa] = useState(false);
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('forhandsvisa');
      if (q === 'bevaka') localStorage.setItem(FLAGGA, '1');
      if (q === 'av') localStorage.removeItem(FLAGGA);
      setPa(localStorage.getItem(FLAGGA) === '1');
    } catch { /* privat läge */ }
  }, []);
  return pa;
}

/**
 * Bevaka en vara som är slut — väntelistan (API /vantelista, admin /dashboard/vantelista). Ägaren 2026-10-09: namn,
 * telefon, mejl, om kunden kan tänka sig en ersättningsprodukt; regnumret kunden sökt med och tiden följer med.
 * Följer med utan att kunden fyller i: vald bil (regnr, ktype, modell), sökordet om kunden kom från sökningen,
 * sidan, kundkontot om inloggad, språket och annonskällan.
 */
export function BevakaForm({ productId, productName }: { productId: number; productName: string }) {
  const pa = useBevakaPa();
  const { locale } = useLocale();
  const bil = useCartStore((s) => s.selectedVehicle);
  const regnr = useCartStore((s) => s.selectedPlate);
  const kundId = useAuthStore((s) => s.customer?.id ?? null);
  const kundNamn = useAuthStore((s) => (s.customer ? `${s.customer.firstname ?? ''} ${s.customer.lastname ?? ''}`.trim() : ''));
  const kundEpost = useAuthStore((s) => s.customer?.email ?? '');
  const [oppen, setOppen] = useState(false);
  const [f, setF] = useState({ namn: '', telefon: '', epost: '', antal: 1, ersattning: false, meddelande: '', samtycke: false });
  const [skickar, setSkickar] = useState(false);
  const [klar, setKlar] = useState(false);
  const [fel, setFel] = useState<string | null>(null);

  useEffect(() => { setF((x) => ({ ...x, namn: x.namn || kundNamn, epost: x.epost || kundEpost })); }, [kundNamn, kundEpost]);

  if (!pa) return <StockNotifyForm productId={productId} productName={productName} />;

  const skicka = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.telefon.trim() && !f.epost.trim()) { setFel(t(locale, 'bevaka.minstEn')); return; }
    setSkickar(true); setFel(null);
    try {
      let sokord: string | null = null;
      try { const r = new URL(document.referrer); if (r.pathname.endsWith('/sok')) sokord = r.searchParams.get('q'); } catch { /* ingen referrer */ }
      const res = await fetch(`${API_URL}/vantelista`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId, ...f, samtycke: f.samtycke,
          regnr, ktype: bil?.ktype ?? null, bil: bil ? `${bil.manufacturer} ${bil.model} ${bil.cartype ?? ''}`.trim() : null,
          sokord, kom_fran: document.referrer || null, sida: window.location.pathname, customer_id: kundId, locale,
          utm: getTrackingData(),
        }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || j.error || ''); }
      setKlar(true);
    } catch (err) { setFel((err as Error).message || t(locale, 'bevaka.fel')); }
    finally { setSkickar(false); }
  };

  if (klar) {
    return (
      <div id="bevaka" className="rounded-xl border-2 border-green-300 bg-green-50 p-4">
        <p className="flex items-center gap-2 font-semibold text-green-900"><Check className="h-5 w-5" />{t(locale, 'bevaka.tack')}</p>
        <p className="mt-0.5 text-sm text-green-800">{t(locale, 'bevaka.tackText')}</p>
      </div>
    );
  }
  if (!oppen) {
    return (
      <button id="bevaka" type="button" onClick={() => setOppen(true)}
        className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-brand bg-white px-4 font-semibold text-brand hover:bg-brand/5">
        <Bell className="h-4 w-4" />{t(locale, 'bevaka.knapp')}
      </button>
    );
  }
  const falt = 'h-11 w-full rounded-lg border border-border bg-white px-3 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand/20';
  return (
    <form id="bevaka" onSubmit={skicka} className="space-y-3 rounded-xl border-2 border-brand/30 bg-cream/30 p-4">
      <div>
        <p className="font-heading font-semibold">{t(locale, 'bevaka.rubrik')}</p>
        <p className="text-xs text-muted-foreground">{t(locale, 'bevaka.ingress')}</p>
        {bil && <p className="mt-1 text-xs">{t(locale, 'bevaka.bil')}: <strong>{regnr ? `${regnr} · ` : ''}{bil.manufacturer} {bil.model}</strong></p>}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="text-xs font-medium sm:col-span-2">{t(locale, 'bevaka.namn')}
          <input id="bevaka-namn" className={falt} autoComplete="name" value={f.namn} onChange={(e) => setF({ ...f, namn: e.target.value })} /></label>
        <label className="text-xs font-medium">{t(locale, 'bevaka.telefon')}
          <input id="bevaka-telefon" className={falt} type="tel" inputMode="tel" autoComplete="tel" value={f.telefon} onChange={(e) => setF({ ...f, telefon: e.target.value })} /></label>
        <label className="text-xs font-medium">{t(locale, 'bevaka.epost')}
          <input id="bevaka-epost" className={falt} type="email" autoComplete="email" value={f.epost} onChange={(e) => setF({ ...f, epost: e.target.value })} /></label>
        <p className="text-[11px] text-muted-foreground sm:col-span-2">{t(locale, 'bevaka.minstEn')}</p>
        <label className="text-xs font-medium">{t(locale, 'bevaka.antal')}
          <input id="bevaka-antal" className={falt} type="number" min={1} max={99} value={f.antal} onChange={(e) => setF({ ...f, antal: Number(e.target.value) || 1 })} /></label>
      </div>
      <label className="flex items-start gap-2 text-sm"><input id="bevaka-ersattning" type="checkbox" className="mt-1" checked={f.ersattning} onChange={(e) => setF({ ...f, ersattning: e.target.checked })} />{t(locale, 'bevaka.ersattning')}</label>
      <label className="block text-xs font-medium">{t(locale, 'bevaka.meddelande')}
        <textarea id="bevaka-meddelande" rows={2} className="w-full rounded-lg border border-border bg-white px-3 py-2 text-base outline-none focus:border-brand" value={f.meddelande} onChange={(e) => setF({ ...f, meddelande: e.target.value })} /></label>
      <label className="flex items-start gap-2 text-sm"><input id="bevaka-samtycke" type="checkbox" required className="mt-1" checked={f.samtycke} onChange={(e) => setF({ ...f, samtycke: e.target.checked })} />{t(locale, 'bevaka.samtycke')}</label>
      {fel && <p className="text-sm text-red-700">{fel}</p>}
      <button type="submit" disabled={skickar}
        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand px-5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">
        {skickar ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}{t(locale, 'bevaka.skicka')}
      </button>
    </form>
  );
}
